import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { AetherProvider } from '#pplx/frontend/aether/components/AetherProvider/index.js';
import { getChipClasses } from '#pplx/frontend/aether/components/Chip/classes.js';
import { DropdownMenu } from '#pplx/frontend/aether/components/DropdownMenu/index.js';
import { Icon } from '#pplx/frontend/aether/components/Icon/index.js';
import { Popover } from '#pplx/frontend/aether/components/Popover/index.js';
import { Tooltip } from '#pplx/frontend/aether/components/Tooltip/index.js';
import { ButtonPrimitive } from '#pplx/frontend/aether/primitives/ButtonPrimitive/index.js';
import { useControllableState } from '#pplx/frontend/common/hooks/useControllableState.js';
import { MODE_TOGGLE_CHEVRON_WIDTH_PX, MODE_TOGGLE_COLLAPSED_WIDTH_PX, } from '#pplx/frontend/composer-sdk-plugins/mode-toggle/types.js';
import { ComposerDropdownCaret } from '#pplx/frontend/composer-sdk/components/ComposerDropdownCaret.js';
import { ComposerContainerContext } from '#pplx/frontend/composer-sdk/ComposerContainerContext.js';
import { animated, useSpring } from '@react-spring/web';
import classNames from 'classnames';
import { memo, useCallback, useContext, useLayoutEffect, useRef, useState } from 'react';
const MATCHED_MENU_GAP_PX = 4;
function ModeToggleChevronSlot({ animationProgress, isVisible, animatedWidth, testIdPrefix, }) {
    return (_jsx(animated.span, { "aria-hidden": true, "data-testid": `${testIdPrefix}-chevron-slot`, className: "inline-flex items-center justify-end overflow-hidden", style: {
            width: animatedWidth ?? (isVisible ? MODE_TOGGLE_CHEVRON_WIDTH_PX : 0),
        }, children: _jsx(animated.span, { className: "ml-1 inline-flex origin-right", style: {
                opacity: animationProgress,
            }, children: _jsx(ComposerDropdownCaret, {}) }) }));
}
function useMatchedMenuLayout({ enabled, isOpen, containerRef, containerSelector, shouldMeasureLayout, framed, }) {
    const triggerRef = useRef(null);
    const anchorRef = useRef(null);
    const { inputBoxRef } = useContext(ComposerContainerContext);
    const [layout, setLayout] = useState(null);
    const resolveAnchorElements = useCallback(() => {
        const triggerElement = triggerRef.current;
        const container = containerRef?.current ??
            (containerSelector != null ? triggerElement?.closest(containerSelector) : null);
        if (!(container instanceof HTMLElement)) {
            anchorRef.current = null;
            return null;
        }
        if (triggerElement == null)
            return null;
        const inputBoxAnchor = framed ? inputBoxRef?.current : null;
        anchorRef.current = inputBoxAnchor
            ? {
                contextElement: container,
                getBoundingClientRect: () => inputBoxAnchor.getBoundingClientRect(),
            }
            : container;
        return { container, triggerElement };
    }, [framed, containerRef, containerSelector, inputBoxRef]);
    const measureLayout = useCallback(() => {
        const anchorElements = resolveAnchorElements();
        if (anchorElements == null)
            return;
        const { container, triggerElement } = anchorElements;
        const triggerRect = triggerElement.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        const nextLayout = {
            widthPx: Math.round(containerRect.width),
            alignOffsetPx: Math.round(containerRect.left - triggerRect.left),
            sideOffsetPx: Math.max(0, Math.round(containerRect.bottom - triggerRect.bottom + MATCHED_MENU_GAP_PX)),
        };
        setLayout((currentLayout) => {
            if (currentLayout?.widthPx === nextLayout.widthPx &&
                currentLayout.alignOffsetPx === nextLayout.alignOffsetPx &&
                currentLayout.sideOffsetPx === nextLayout.sideOffsetPx) {
                return currentLayout;
            }
            return nextLayout;
        });
    }, [resolveAnchorElements]);
    useLayoutEffect(() => {
        if (!enabled) {
            anchorRef.current = null;
            setLayout(null);
            return;
        }
        if (!isOpen)
            return;
        if (!shouldMeasureLayout) {
            resolveAnchorElements();
            return;
        }
        measureLayout();
        const anchorElements = resolveAnchorElements();
        if (anchorElements == null) {
            return;
        }
        const { container, triggerElement } = anchorElements;
        window.addEventListener('resize', measureLayout);
        window.addEventListener('scroll', measureLayout, true);
        if (!('ResizeObserver' in window)) {
            return () => {
                window.removeEventListener('resize', measureLayout);
                window.removeEventListener('scroll', measureLayout, true);
            };
        }
        const observer = new ResizeObserver(measureLayout);
        observer.observe(container);
        observer.observe(triggerElement);
        return () => {
            window.removeEventListener('resize', measureLayout);
            window.removeEventListener('scroll', measureLayout, true);
            observer.disconnect();
        };
    }, [enabled, isOpen, measureLayout, resolveAnchorElements, shouldMeasureLayout]);
    return { triggerRef, anchorRef, layout };
}
export const ModeToggle = memo(function ModeToggle({ label, ariaLabel, icon, isOn, onActivate, offTooltip, offTooltipSubtitle, lockedReason, allowMenuWhenLocked = false, renderMenu, open, onOpenChange, animationProgress: controlledAnimationProgress, animateChevronWidth = false, onBaseWidthChange, labelVisibility = 'active', overlayKind = 'menu', matchMenuToContainerWidth = false, appearance = 'default', layoutContainerRef, containerSelector, testIdPrefix = 'mode-toggle', }) {
    const controlsExternalMenu = (renderMenu === null || renderMenu === undefined) && open !== undefined;
    const [menuOpen, handleMenuToggle] = useControllableState({
        value: open,
        defaultValue: false,
        onChange: onOpenChange,
    });
    const shouldMatchMenuToContainer = matchMenuToContainerWidth && isOn && renderMenu != null;
    const matchedMenuLayout = useMatchedMenuLayout({
        enabled: shouldMatchMenuToContainer,
        isOpen: menuOpen,
        containerRef: layoutContainerRef,
        containerSelector,
        shouldMeasureLayout: overlayKind === 'menu',
        framed: appearance !== 'default',
    });
    const [baseContent, setBaseContent] = useState(null);
    useLayoutEffect(() => {
        const button = matchedMenuLayout.triggerRef.current;
        if (!baseContent || !button || !onBaseWidthChange)
            return;
        // Observe only the stable icon/label span so caret animation cannot feed
        // back into the base width. The callback ref rebinds after menu/lock swaps.
        const updateWidth = () => {
            const style = window.getComputedStyle(button);
            onBaseWidthChange(Math.max(MODE_TOGGLE_COLLAPSED_WIDTH_PX, baseContent.getBoundingClientRect().width +
                parseFloat(style.paddingLeft) +
                parseFloat(style.paddingRight) +
                parseFloat(style.borderLeftWidth) +
                parseFloat(style.borderRightWidth)));
        };
        updateWidth();
        const observer = new ResizeObserver(updateWidth);
        observer.observe(baseContent);
        return () => observer.disconnect();
    }, [baseContent, isOn, matchedMenuLayout.triggerRef, onBaseWidthChange]);
    const fallbackSpring = useSpring({
        progress: isOn ? 1 : 0,
        immediate: true,
    });
    const animationProgress = controlledAnimationProgress ?? fallbackSpring.progress;
    const isIconOnly = labelVisibility === 'never';
    const showLabel = labelVisibility === 'always' || (labelVisibility === 'active' && isOn);
    const usePersistentLabelLayout = labelVisibility === 'always';
    const hasDropdown = (renderMenu !== null && renderMenu !== undefined) || controlsExternalMenu;
    const showIconOnlyChevron = isIconOnly && hasDropdown && isOn;
    const locked = lockedReason != null;
    const canOpenLockedDropdown = locked && allowMenuWhenLocked && isOn && hasDropdown;
    const disabled = locked && !canOpenLockedDropdown;
    const handleContainerClick = useCallback(() => {
        if (disabled)
            return;
        if (!isOn) {
            onActivate();
            return;
        }
        if (controlsExternalMenu) {
            handleMenuToggle(!menuOpen);
            return;
        }
        // When on + dropdown: the overlay trigger handles open/close.
    }, [controlsExternalMenu, disabled, handleMenuToggle, isOn, menuOpen, onActivate]);
    const closeMenu = useCallback(() => {
        handleMenuToggle(false);
    }, [handleMenuToggle]);
    const handlePopoverFocusOutside = useCallback((event) => {
        if (containerSelector == null)
            return;
        const target = event.target;
        if (!(target instanceof HTMLElement))
            return;
        if (!target.closest(containerSelector))
            return;
        // Keep input focus from dismissing the command menu while the input filters it.
        event.preventDefault();
    }, [containerSelector]);
    const chip = (_jsx(ButtonPrimitive, { interactableVariant: "alt", className: classNames(getChipClasses({
            variant: 'suggestion',
            visuallySelected: isOn,
            rounded: true,
            disabled,
            leadingAccessory: true,
        }), 
        // Override gap/padding so the off state stays compact and the animated
        // label/chevron opens cleanly to the right of the icon.
        'relative gap-0 pr-2 overflow-visible !border-solid !border-transparent !bg-transparent hover:!bg-transparent', animateChevronWidth && '!gap-0', 
        // The on-state visual is provided by a sliding indicator pill rendered
        // behind both toggles in ModeToggleGroup. Suppress the chip's own filled
        // background so the pill shows through.
        isOn && '!bg-transparent', isOn ? 'text-primary' : 'text-secondary hover:text-primary'), style: {
            minWidth: MODE_TOGGLE_COLLAPSED_WIDTH_PX,
            // The persistent-label variant sizes to its content. Hidden-label
            // states collapse to the icon-only width.
            width: isIconOnly
                ? undefined
                : !usePersistentLabelLayout && !showLabel
                    ? MODE_TOGGLE_COLLAPSED_WIDTH_PX
                    : undefined,
        }, onClick: handleContainerClick, disabled: disabled, "aria-pressed": isOn, "aria-haspopup": controlsExternalMenu ? 'menu' : undefined, "aria-expanded": controlsExternalMenu ? menuOpen : undefined, "aria-label": !showLabel || !label ? label || ariaLabel : undefined, ref: matchedMenuLayout.triggerRef, children: animateChevronWidth ? (_jsxs(_Fragment, { children: [_jsxs("span", { ref: setBaseContent, className: "inline-flex items-center gap-1 whitespace-nowrap", children: [_jsx("span", { className: "inline-flex items-center p-two", children: _jsx(Icon, { icon: icon, size: "tiny" }) }), label] }), _jsx(ModeToggleChevronSlot, { animationProgress: animationProgress, animatedWidth: animationProgress.to((progress) => MODE_TOGGLE_CHEVRON_WIDTH_PX * progress), isVisible: isOn && hasDropdown, testIdPrefix: testIdPrefix })] })) : (_jsxs(_Fragment, { children: [_jsx("span", { className: "inline-flex items-center p-two", children: _jsx(Icon, { icon: icon, size: "tiny" }) }), isIconOnly ? null : usePersistentLabelLayout ? (_jsxs("span", { className: "inline-flex items-center whitespace-nowrap", children: [label, hasDropdown && (_jsx(ModeToggleChevronSlot, { animationProgress: hasDropdown ? animationProgress : 0, isVisible: isOn && hasDropdown, testIdPrefix: testIdPrefix }))] })) : (_jsxs(animated.span, { "aria-hidden": !showLabel, className: "inline-flex items-center gap-1 whitespace-nowrap", style: {
                        opacity: animationProgress,
                    }, children: [label, hasDropdown && _jsx(ComposerDropdownCaret, {})] })), showIconOnlyChevron && (_jsx(ModeToggleChevronSlot, { animationProgress: animationProgress, isVisible: true, testIdPrefix: testIdPrefix }))] })) }));
    if (disabled && lockedReason != null) {
        return (_jsx(Tooltip, { content: lockedReason, children: _jsx("span", { className: "inline-flex", children: chip }) }));
    }
    const chipWithHoverTooltip = isOn || offTooltip == null ? (chip) : (_jsx(Tooltip, { content: offTooltip, subtitle: offTooltipSubtitle, delayDurationMs: 400, children: _jsx("span", { className: "inline-flex", style: usePersistentLabelLayout ? undefined : { width: MODE_TOGGLE_COLLAPSED_WIDTH_PX }, children: chip }) }));
    // Only the active toggle mounts an overlay; an inactive toggle that still
    // received a `renderMenu` skips it via `!isOn`.
    if (renderMenu == null || !isOn) {
        return chipWithHoverTooltip;
    }
    if (overlayKind === 'popover') {
        const shouldUseMatchedAnchor = matchMenuToContainerWidth;
        // The anchor preserves container width and the requested vertical alignment.
        const matchedAnchorRef = shouldUseMatchedAnchor ? matchedMenuLayout.anchorRef : undefined;
        const popover = (_jsx(Popover, { open: menuOpen, onOpenChange: handleMenuToggle, side: "bottom", align: "start", offsetPx: shouldUseMatchedAnchor ? MATCHED_MENU_GAP_PX : undefined, triggerElement: chipWithHoverTooltip, anchorRef: matchedAnchorRef, onFocusOutside: handlePopoverFocusOutside, 
            // The input remains the active surface while the menu is open.
            shouldAutoFocus: false, 
            // The popover overlay is used for the command menu, which should appear
            // and disappear immediately while filtering from input.
            disableAnimation: true, children: _jsx(_Fragment, { children: renderMenu({ closeMenu }) }) }));
        if (!shouldUseMatchedAnchor)
            return popover;
        return (_jsx(AetherProvider, { isMobileStyle: false, renderModals: false, children: popover }));
    }
    return (_jsx(DropdownMenu, { isOpen: menuOpen, onToggle: handleMenuToggle, side: "bottom", align: "start", offset: matchedMenuLayout.layout?.sideOffsetPx, alignOffset: matchedMenuLayout.layout?.alignOffsetPx, minWidthPx: matchedMenuLayout.layout?.widthPx, maxWidthPx: matchedMenuLayout.layout?.widthPx, triggerElement: chipWithHoverTooltip, children: _jsx(_Fragment, { children: renderMenu({ closeMenu }) }) }));
});
//# sourceMappingURL=ModeToggle.js.map